(() => {
  if (customElements.get('fr-fortieth-reflection')) return;

  customElements.define('fr-fortieth-reflection', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.surface = this.querySelector('[data-fr40-surface]');
      this.ripple = this.querySelector('[data-fr40-ripple]');
      this.message = this.querySelector('[data-fr40-message]');
      this.next = this.querySelector('[data-fr40-next]');
      this.controls = this.querySelector('[data-fr40-controls]');
      this.revealButton = this.querySelector('[data-fr40-reveal]');
      this.hint = this.querySelector('[data-fr40-hint]');
      this.revealStatus = this.querySelector('[data-fr40-reveal-status]');
      this.interactive = this.querySelector('[data-fr40-interactive]');
      this.fallback = this.querySelector('[data-fr40-fallback]');
      this.response = this.querySelector('[data-fr40-response]');
      this.support = this.querySelector('[data-fr40-support]');
      this.choices = [...this.querySelectorAll('[data-fr40-choice]')];
      if (![this.surface, this.ripple, this.message, this.next, this.controls, this.revealButton, this.hint, this.revealStatus, this.interactive, this.fallback, this.response, this.support].every(Boolean) || this.choices.length !== 4) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotion = () => {
        if (this.motion.matches) {
          this.cancelWater();
          this.reveal(false);
        }
        this.hint.hidden = this.motion.matches || this.hasAttribute('data-fr40-revealed');
      };
      this.motion.addEventListener('change', this.onMotion);
      this.revealButton.addEventListener('click', () => this.reveal(true), { signal });
      this.choices.forEach(button => button.addEventListener('click', () => {
        this.choices.forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
        this.response.textContent = button.dataset.response;
        this.support.hidden = button.dataset.fr40Choice !== 'reach-out';
      }, { signal }));
      this.surface.addEventListener('pointerdown', event => this.touchWater(event), { passive: true, signal });
      this.surface.addEventListener('pointermove', event => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') this.touchWater(event);
      }, { passive: true, signal });
      this.surface.addEventListener('pointerleave', () => {
        if (this.settleTimer) this.queueReveal(400);
      }, { passive: true, signal });
      this.surface.addEventListener('pointercancel', () => this.cancelWater(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.cancelWater();
      }, { signal });
      window.addEventListener('pagehide', () => this.cancelWater(), { signal });
      this.panelObserver = new MutationObserver(() => {
        if (this.closest('[hidden]')) this.cancelWater();
      });
      const panel = this.closest('[data-lock-panel]');
      if (panel) this.panelObserver.observe(panel, { attributes: true, attributeFilter: ['hidden'] });

      this.fallback.hidden = true;
      this.interactive.hidden = false;
      this.controls.hidden = false;
      this.setAttribute('data-fr40-ready', '');
      if (this.motion.matches || this.hasAttribute('data-fr40-revealed')) this.reveal(false);
      else {
        this.message.hidden = true;
        this.next.hidden = true;
      }
      this.onMotion();
    }

    touchWater(event) {
      if (this.motion.matches || this.closest('[hidden]') || event.target.closest('button, a') || !event.isPrimary) return;
      const now = performance.now();
      if (now - (this.lastRipple || 0) > 180) {
        const bounds = this.surface.getBoundingClientRect();
        this.ripple.style.left = `${Math.max(0, Math.min(bounds.width, event.clientX - bounds.left))}px`;
        this.ripple.style.top = `${Math.max(0, Math.min(bounds.height, event.clientY - bounds.top))}px`;
        this.rippleAnimation?.cancel();
        this.rippleAnimation = this.ripple.animate([
          { opacity: .65, transform: 'translate(-50%, -50%) scale(.22)' },
          { opacity: 0, transform: 'translate(-50%, -50%) scale(1.8)' }
        ], { duration: 1100, easing: 'cubic-bezier(.15,.55,.3,1)' });
        this.lastRipple = now;
      }
      if (!this.hasAttribute('data-fr40-revealed')) this.queueReveal(950);
    }

    queueReveal(delay) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => {
        this.settleTimer = null;
        if (!document.hidden && !this.closest('[hidden]')) this.reveal(true);
      }, delay);
    }

    reveal(announce) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
      const firstReveal = !this.hasAttribute('data-fr40-revealed');
      this.setAttribute('data-fr40-revealed', '');
      this.message.hidden = false;
      this.next.hidden = false;
      this.hint.hidden = true;
      this.revealButton.setAttribute('aria-expanded', 'true');
      this.revealButton.textContent = this.revealButton.dataset.revealedLabel;
      if (announce && firstReveal) this.revealStatus.textContent = this.message.textContent;
    }

    cancelWater() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.rippleAnimation?.cancel();
    }

    disconnectedCallback() {
      this.cancelWater();
      this.controller?.abort();
      this.controller = null;
      this.panelObserver?.disconnect();
      this.motion?.removeEventListener('change', this.onMotion);
    }
  });
})();
